/**
 * Impostazioni, sezione «Gestionale»: il collegamento con Guesty e le sue
 * chiavi.
 */

import { useEffect, useState } from "react";
import {
  Check,
  Gauge,
  KeyRound,
  PlugZap,
  RefreshCw,
  Ticket,
} from "lucide-react";
import { api } from "../../lib/api.ts";
import { messaggioDi } from "../../lib/errori.ts";
import { useStore } from "../../lib/store.tsx";
import { Badge, Card, Pill } from "../../ui/primitives.tsx";
import { Campo } from "../Accesso.tsx";
import { Scheda } from "./comuni.tsx";

export function SezioneGestionale() {
  const store = useStore();
  const account = store.account;
  const [finale, setFinale] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [cambio, setCambio] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [provando, setProvando] = useState(false);

  useEffect(() => {
    void api
      .onboarding()
      .then((r) => setFinale(r.onboarding.gestionale.clientIdFinale))
      .catch(() => setFinale(null));
  }, [account?.pmsConnectedAt]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { report } = await api.sync();
      store.notify(
        `Aggiornato: ${report["listings"]} immobili, ${report["calendarDays"]} notti.`,
      );
    } catch (errore) {
      // Il motivo intero: il backend dice già «riprova fra 40 secondi» o «il
      // gestionale ha risposto 401».
      store.notify(
        messaggioDi(
          errore,
          "La sincronizzazione non è riuscita. Il gestionale risponde?",
        ),
      );
    } finally {
      setSyncing(false);
    }
  };

  const salvaChiavi = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrore(null);
    setProvando(true);
    try {
      const r = await api.collegaGestionale(
        clientId.trim(),
        clientSecret.trim(),
      );
      await store.refreshAccount();
      store.notify(
        `Chiavi aggiornate: Guesty risponde, ${r.immobili} immobili. Rileggi tutto per aggiornare i dati.`,
      );
      setCambio(false);
      setClientId("");
      setClientSecret("");
    } catch (e) {
      setErrore(messaggioDi(e));
    } finally {
      setProvando(false);
    }
  };

  const collegatoIl = account?.pmsConnectedAt
    ? new Intl.DateTimeFormat("it-IT", { dateStyle: "long" }).format(
        new Date(account.pmsConnectedAt),
      )
    : null;

  return (
    <>
      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-4">
          <span className="grid size-12 shrink-0 place-items-center rounded-card-lg bg-surface-muted text-ink">
            <PlugZap size={20} />
          </span>
          <div className="min-w-[220px] flex-1">
            <p className="flex items-center gap-2 text-[16px] font-extrabold capitalize text-ink">
              {account?.pmsType === "mock" ? "Simulatore" : "Guesty"}
              {account?.pmsConnected ? (
                <Badge tone="ok">collegato</Badge>
              ) : (
                <Badge tone="warn">non collegato</Badge>
              )}
            </p>
            <p className="mt-0.5 text-[13px] text-ink-soft">
              {collegatoIl ? `Dal ${collegatoIl}` : "Nessuna chiave salvata"}
              {finale ? ` · Client ID ••••${finale}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Pill
              variant="secondary"
              size="sm"
              icon={<RefreshCw size={14} />}
              onClick={sync}
              loading={syncing}
              disabled={!account?.pmsConnected}
            >
              Rileggi tutto
            </Pill>
            {cambio ? null : (
              <Pill
                variant="ghost"
                size="sm"
                icon={<KeyRound size={14} />}
                onClick={() => setCambio(true)}
              >
                Cambia chiavi
              </Pill>
            )}
          </div>
        </div>

        {account?.pmsType === "mock" ? (
          <p className="mt-4 rounded-card bg-surface-muted px-3.5 py-2.5 text-[13px] leading-relaxed text-ink-soft">
            Stai usando il <b>simulatore</b>: un gestionale finto per provare
            tutto, comprese le modifiche, senza toccare dati reali.
          </p>
        ) : null}

        {cambio ? (
          <form
            onSubmit={salvaChiavi}
            className="animate-rise mt-5 flex max-w-[460px] flex-col gap-3.5 border-t border-line pt-5"
          >
            <Campo
              id="nuovo-client-id"
              label="Client ID"
              icona={<Ticket size={15} />}
              value={clientId}
              onChange={setClientId}
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
            <Campo
              id="nuovo-client-secret"
              label="Client Secret"
              icona={<KeyRound size={15} />}
              value={clientSecret}
              onChange={setClientSecret}
              type="password"
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-[12.5px] leading-relaxed text-ink-faint">
              Le provo subito leggendo gli immobili. Se Guesty le rifiuta, non
              le salvo e restano quelle di prima… anzi, nessuna: rimettile.
            </p>
            {errore ? (
              <p
                role="alert"
                className="rounded-card bg-danger-soft px-3.5 py-2.5 text-[13px] font-semibold text-danger"
              >
                {errore}
              </p>
            ) : null}
            <div className="flex gap-2">
              <Pill
                variant="primary"
                size="md"
                type="submit"
                loading={provando}
                icon={<Check size={15} />}
              >
                Prova e salva
              </Pill>
              <Pill variant="ghost" size="md" onClick={() => setCambio(false)}>
                Annulla
              </Pill>
            </div>
          </form>
        ) : null}
      </Card>

      <Scheda
        Icona={Gauge}
        titolo="Come resta aggiornato"
        testo="Quando qualcosa cambia su Guesty arriva un avviso in tempo reale, se il server è raggiungibile da internet. In più, ogni mezz'ora Otto ricontrolla tutto da solo, e una volta al giorno rilegge i mesi passati."
      />
    </>
  );
}
